/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Flip } from '@gfazioli/mantine-flip';
import {
  ActionIcon,
  Alert,
  Badge,
  Group,
  Paper,
  Select,
  Space,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconRecycle,
  IconSchema,
  IconTableRow,
  IconTarget,
  IconTruck,
  IconZoomMoney,
} from '@tabler/icons-react';
import { Tabs } from 'antd';
import {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ReferenciaCodigo, TipoCodigo } from '../../../../business/events/general';
import ProfileCardLink from '../../../../components/core/ProfileCardLink/ProfileCardLink';
import { useEventGeneralContext } from '../../../../contexts/core/events/EventGeneral.context';
import { EntityTypeType, UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import {
  EventDestinationQuotationType,
  EventDestinationType,
  EventEquipmentQuotationType,
  EventEquipmentType,
  EventPackagingQuotationType,
  EventPackagingType,
  EventTreatmentQuotationType,
  EventTreatmentType,
  EventType,
  EventVehicleQuotationType,
  EventVehicleType,
} from '../../../../models/core/events.type';
import {
  ProposalResiduePlanCodeEnum,
  ProposalResiduePlanDestinationType,
  ProposalResiduePlanEquipmentType,
  ProposalResiduePlanPackagingType,
  ProposalResiduePlanQuotationDestinationType,
  ProposalResiduePlanQuotationEquipmentType,
  ProposalResiduePlanQuotationPackagingType,
  ProposalResiduePlanQuotationTreatmentType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanQuotationVehicleType,
  ProposalResiduePlanTreatmentType,
  ProposalResiduePlanType,
  ProposalResiduePlanVehicleType,
  ProposalResidueType,
} from '../../../../models/core/proposals.type';
import { Feature } from '../../../../utils/constants.utils';
import { buildFakeAuditObject } from '../../../../utils/helper.utils';
import { transformComboBoxGroupDataFromV5toV7 } from '../../../../utils/mantine.utils';
import ResiduePlanSummary from '../../proposals/components/ResiduePlanSummary';
import { buildSelectQuotationItem } from '../EventAddEdit/components/PlanQuotationSelectItem/PlanQuotationSelectItem';
import EventDestinationView from '../EventView/components/ReferenceView/Destination/EventDestinationView';
import EventEquipmentView from '../EventView/components/ReferenceView/Equipment/EventEquipmentView';
import EventPackagingView from '../EventView/components/ReferenceView/Packaging/EventPackagingView';
import EventTreatmentView from '../EventView/components/ReferenceView/Treatment/EventTreatmentView';
import EventVehicleView from '../EventView/components/ReferenceView/Vehicle/EventVehicleView';

type EventResidueReferenceEditorProps = {
  referenceData: {
    entityTypeData: EntityTypeType[];
    unitOfMeasures: UnitOfMeasureType[];
  };
  residue: ProposalResidueType | null;
  event: EventType | null;
  selectedPlanId?: number | null;
  selectedQuotationId?: number | null;
  forceSingleCustomFlow?: boolean;
  disabled?: boolean;
  statusSoftDisabled?: boolean;
  zIndex: number;
};

type ReferenceFlipCardProps = {
  header: ReactNode;
  frontContent: ReactNode;
  backContent: ReactNode;
};

function ReferenceFlipCard(props: ReferenceFlipCardProps) {
  const { backContent, frontContent, header } = props;
  const [flipped, setFlipped] = useState<boolean>(false);
  const [height, setHeight] = useState(320);
  const frontRef = useRef<HTMLDivElement | null>(null);
  const backRef = useRef<HTMLDivElement | null>(null);

  const updateHeight = () => {
    const nextHeight = Math.max(frontRef.current?.scrollHeight || 0, backRef.current?.scrollHeight || 0);

    if (nextHeight > 0) {
      setHeight(nextHeight);
    }
  };

  useLayoutEffect(() => {
    updateHeight();
  });

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => updateHeight());

    if (frontRef.current) {
      observer.observe(frontRef.current);
    }

    if (backRef.current) {
      observer.observe(backRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Stack gap="sm">
      <Group justify="space-between" onDoubleClick={() => setFlipped((current) => !current)}>
        {header}
        <Tooltip label={flipped ? 'Voltar para visual novo' : 'Abrir visual legado'} withArrow>
          <ActionIcon
            size="sm"
            variant="light"
            color={flipped ? 'grape' : 'blue'}
            onClick={() => setFlipped((current) => !current)}
          >
            {flipped ? <IconSchema size={15} /> : <IconTableRow size={15} />}
          </ActionIcon>
        </Tooltip>
      </Group>

      <Flip flipped={flipped} onChange={setFlipped} h={height} w="100%" perspective={5000} duration={0.25}>
        <Flip.Front>
          <div ref={frontRef}>{frontContent}</div>
        </Flip.Front>

        <Flip.Back>
          <div ref={backRef}>{backContent}</div>
        </Flip.Back>
      </Flip>
    </Stack>
  );
}

const findPlan = (
  residue: ProposalResidueType | null,
  selectedPlanId?: number | null
): ProposalResiduePlanType | null => {
  if (!residue) {
    return null;
  }

  if (selectedPlanId) {
    return residue.planos.find((x) => Number(x.idPropostaResiduoPlano) === Number(selectedPlanId)) || null;
  }

  return residue.planos.find((x) => x.primario) || residue.planos[0] || null;
};

const buildEmptyQuotation = (): ProposalResiduePlanQuotationType => ({
  ...buildFakeAuditObject(),
  id: undefined,
  action: Action.Add,
  idTemporaria: undefined,
  idPropostaResiduoPlanoCotacao: -1,
  idPropostaResiduoPlano: -1,
  primario: true,
  observacao: null,
  acondicionamentos: [],
  equipamentos: [],
  veiculo: null as any,
  tratamento: null as any,
  destinoFinal: null as any,
  completo: false,
});

const buildEmptyPlan = (
  residue: ProposalResidueType,
  codigoPlanoTipo: ProposalResiduePlanCodeEnum
): ProposalResiduePlanType => ({
  ...buildFakeAuditObject(),
  id: undefined,
  action: Action.Add,
  idTemporaria: undefined,
  idPropostaResiduoPlano: -1,
  idPropostaResiduo: residue.idPropostaResiduo || -1,
  codigoPlanoTipo,
  primario: true,
  observacao: null,
  acondicionamentos: [],
  equipamentos: [],
  veiculo: null,
  tratamento: null,
  destinoFinal: null,
  cotacoes: [buildEmptyQuotation()],
});

const buildPackagingOrEquipmentQuotationCompatibility = <
  T extends ProposalResiduePlanQuotationPackagingType | ProposalResiduePlanQuotationEquipmentType | null,
  E extends EventPackagingQuotationType | EventEquipmentQuotationType | null
>(
  proposalQuotation: T,
  eventQuotation: T,
  compra: boolean,
  origItem: E
): Partial<EventPackagingQuotationType & EventEquipmentQuotationType> => {
  if (!proposalQuotation || !eventQuotation) {
    return {
      precoReferencia: origItem?.precoReferencia ?? null,
      precoReferenciaCompativel: false,
    };
  }

  return {
    precoReferencia: proposalQuotation.precificacao?.preco ?? origItem?.precoReferencia ?? null,
    precoReferenciaCompativel:
      proposalQuotation.quantidade === eventQuotation.quantidade &&
      proposalQuotation.quantidadeIdUnidadeMedida === eventQuotation.quantidadeIdUnidadeMedida &&
      proposalQuotation.frequenciaIdUnidadeMedida === eventQuotation.frequenciaIdUnidadeMedida,
  };
};

const buildVehicleQuotationCompatibility = (
  proposalQuotation: ProposalResiduePlanQuotationVehicleType | null,
  eventQuotation: ProposalResiduePlanQuotationVehicleType | null,
  compra: boolean,
  origItem: EventVehicleQuotationType | null
): Partial<EventVehicleQuotationType> => {
  if (!proposalQuotation || !eventQuotation) {
    return {
      precoReferencia: origItem?.precoReferencia ?? null,
      precoReferenciaCompativel: false,
    };
  }

  return {
    precoReferencia: proposalQuotation.precificacao?.preco ?? origItem?.precoReferencia ?? null,
    precoReferenciaCompativel:
      proposalQuotation.quantidade === eventQuotation.quantidade &&
      proposalQuotation.quantidadeIdUnidadeMedida === eventQuotation.quantidadeIdUnidadeMedida &&
      proposalQuotation.frequenciaIdUnidadeMedida === eventQuotation.frequenciaIdUnidadeMedida,
  };
};

const buildTreatmentQuotationCompatibility = (
  proposalQuotation: ProposalResiduePlanQuotationTreatmentType | null,
  eventQuotation: ProposalResiduePlanQuotationTreatmentType | null,
  compra: boolean,
  origItem: EventTreatmentQuotationType | null
): Partial<EventTreatmentQuotationType> => {
  if (!proposalQuotation || !eventQuotation) {
    return {
      precoReferencia: origItem?.precoReferencia ?? null,
      precoReferenciaCompativel: false,
    };
  }

  return {
    precoReferencia: proposalQuotation.precificacao?.preco ?? origItem?.precoReferencia ?? null,
    precoReferenciaCompativel:
      proposalQuotation.quantidade === eventQuotation.quantidade &&
      proposalQuotation.quantidadeIdUnidadeMedida === eventQuotation.quantidadeIdUnidadeMedida,
  };
};

const buildDestinationQuotationCompatibility = (
  proposalQuotation: ProposalResiduePlanQuotationDestinationType | null,
  eventQuotation: ProposalResiduePlanQuotationDestinationType | null,
  compra: boolean,
  origItem: EventDestinationQuotationType | null
): Partial<EventDestinationQuotationType> => {
  if (!proposalQuotation || !eventQuotation) {
    return {
      precoReferencia: origItem?.precoReferencia ?? null,
      precoReferenciaCompativel: false,
    };
  }

  return {
    precoReferencia: proposalQuotation.precificacao?.preco ?? origItem?.precoReferencia ?? null,
    precoReferenciaCompativel:
      proposalQuotation.quantidade === eventQuotation.quantidade &&
      proposalQuotation.quantidadeIdUnidadeMedida === eventQuotation.quantidadeIdUnidadeMedida,
  };
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const buildEventPackagingFromProposal = (
  item: ProposalResiduePlanPackagingType | null,
  quotation: ProposalResiduePlanQuotationPackagingType | null,
  proposalQuotation: ProposalResiduePlanQuotationPackagingType | null,
  compra: boolean,
  origItem: EventPackagingType | null
): EventPackagingType | null => {
  if (!item || !quotation) {
    return null;
  }

  const quotationCompatibility = buildPackagingOrEquipmentQuotationCompatibility(
    proposalQuotation,
    quotation,
    compra,
    origItem?.cotacao || null
  );

  return {
    ...buildFakeAuditObject(),
    ...(origItem || {}),
    id: item.id,
    idTemporaria: item.idTemporaria,
    idEventoAcondicionamento: origItem?.idEventoAcondicionamento || -1,
    action: origItem?.action || item.action || (origItem ? Action.Nothing : Action.Add),
    idEvento: origItem?.idEvento || null,
    idResiduoAcondicionamento: Number(item.idResiduoAcondicionamento),
    residuoAcondicionamento: item.residuoAcondicionamento,
    residuoAcondicionamentoDescricao: item.residuoAcondicionamentoDescricao,
    quantidade: Number(item.quantidade),
    quantidadeIdUnidadeMedida: Number(item.quantidadeIdUnidadeMedida),
    quantidadeUnidadeMedida: item.quantidadeUnidadeMedida,
    quantidadeUnidadeMedidaSigla: item.quantidadeUnidadeMedidaSigla,
    precificacao: item.precificacao,
    observacao: item.observacao?.trim() || null,
    cotacao: {
      ...buildFakeAuditObject(),
      ...(origItem?.cotacao || {}),
      id: quotation.id,
      idTemporaria: quotation.idTemporaria,
      idEventoAcondicionamentoCotacao: origItem?.cotacao?.idEventoAcondicionamentoCotacao || -1,
      idEventoAcondicionamento: origItem?.idEventoAcondicionamento || -1,
      action:
        origItem?.cotacao?.action || quotation.action || (origItem?.cotacao ? Action.Nothing : Action.Add),
      idEntidadeResiduoAcondicionamento: toNumberOrNull(quotation.idEntidadeResiduoAcondicionamento),
      idResiduoAcondicionamento: Number(quotation.idResiduoAcondicionamento),
      residuoAcondicionamento: quotation.residuoAcondicionamento,
      residuoAcondicionamentoDescricao: quotation.residuoAcondicionamentoDescricao,
      idFornecedor: toNumberOrNull(quotation.idFornecedor),
      fornecedorCNPJ: quotation.fornecedorCNPJ,
      fornecedorRazaoSocial: quotation.fornecedorRazaoSocial,
      fornecedorNomeFantasia: quotation.fornecedorNomeFantasia,
      fornecedorCPF: quotation.fornecedorCPF,
      fornecedorNome: quotation.fornecedorNome,
      quantidade: toNumberOrNull(quotation.quantidade),
      quantidadeIdUnidadeMedida: toNumberOrNull(quotation.quantidadeIdUnidadeMedida),
      quantidadeUnidadeMedida: quotation.quantidadeUnidadeMedida || null,
      quantidadeUnidadeMedidaSigla: quotation.quantidadeUnidadeMedidaSigla || null,
      frequenciaIdUnidadeMedida: toNumberOrNull(quotation.frequenciaIdUnidadeMedida),
      frequenciaUnidadeMedida: quotation.frequenciaUnidadeMedida || null,
      frequenciaUnidadeMedidaSigla: quotation.frequenciaUnidadeMedidaSigla || null,
      preco: toNumberOrNull(quotation.preco),
      cobrarSemUso: !!quotation.cobrarSemUso,
      condicaoLocacao: quotation.condicaoLocacao || null,
      precificacao: quotation.precificacao || origItem?.cotacao?.precificacao || item.precificacao,
      observacao: quotation.observacao?.trim() || null,
      precoReferencia: quotationCompatibility.precoReferencia ?? null,
      precoReferenciaCompativel: !!quotationCompatibility.precoReferenciaCompativel,
      tolerancias: quotation.tolerancias || [],
    },
  } as EventPackagingType;
};

const buildEventEquipmentFromProposal = (
  item: ProposalResiduePlanEquipmentType | null,
  quotation: ProposalResiduePlanQuotationEquipmentType | null,
  proposalQuotation: ProposalResiduePlanQuotationEquipmentType | null,
  compra: boolean,
  origItem: EventEquipmentType | null
): EventEquipmentType | null => {
  if (!item || !quotation) {
    return null;
  }

  const quotationCompatibility = buildPackagingOrEquipmentQuotationCompatibility(
    proposalQuotation,
    quotation,
    compra,
    origItem?.cotacao || null
  );

  return {
    ...buildFakeAuditObject(),
    ...(origItem || {}),
    id: item.id,
    idTemporaria: item.idTemporaria,
    idEventoEquipamento: origItem?.idEventoEquipamento || -1,
    action: origItem?.action || item.action || (origItem ? Action.Nothing : Action.Add),
    idEvento: origItem?.idEvento || null,
    idResiduoEquipamento: Number(item.idResiduoEquipamento),
    residuoEquipamento: item.residuoEquipamento,
    residuoEquipamentoDescricao: item.residuoEquipamentoDescricao,
    quantidade: Number(item.quantidade),
    quantidadeIdUnidadeMedida: Number(item.quantidadeIdUnidadeMedida),
    quantidadeUnidadeMedida: item.quantidadeUnidadeMedida,
    quantidadeUnidadeMedidaSigla: item.quantidadeUnidadeMedidaSigla,
    precificacao: item.precificacao,
    observacao: item.observacao?.trim() || null,
    cotacao: {
      ...buildFakeAuditObject(),
      ...(origItem?.cotacao || {}),
      id: quotation.id,
      idTemporaria: quotation.idTemporaria,
      idEventoEquipamentoCotacao: origItem?.cotacao?.idEventoEquipamentoCotacao || -1,
      idEventoEquipamento: origItem?.idEventoEquipamento || -1,
      action:
        origItem?.cotacao?.action || quotation.action || (origItem?.cotacao ? Action.Nothing : Action.Add),
      idEntidadeResiduoEquipamento: toNumberOrNull(quotation.idEntidadeResiduoEquipamento),
      idResiduoEquipamento: Number(quotation.idResiduoEquipamento),
      residuoEquipamento: quotation.residuoEquipamento,
      residuoEquipamentoDescricao: quotation.residuoEquipamentoDescricao,
      idFornecedor: toNumberOrNull(quotation.idFornecedor),
      fornecedorCNPJ: quotation.fornecedorCNPJ,
      fornecedorRazaoSocial: quotation.fornecedorRazaoSocial,
      fornecedorNomeFantasia: quotation.fornecedorNomeFantasia,
      fornecedorCPF: quotation.fornecedorCPF,
      fornecedorNome: quotation.fornecedorNome,
      quantidade: toNumberOrNull(quotation.quantidade),
      quantidadeIdUnidadeMedida: toNumberOrNull(quotation.quantidadeIdUnidadeMedida),
      quantidadeUnidadeMedida: quotation.quantidadeUnidadeMedida || null,
      quantidadeUnidadeMedidaSigla: quotation.quantidadeUnidadeMedidaSigla || null,
      frequenciaIdUnidadeMedida: toNumberOrNull(quotation.frequenciaIdUnidadeMedida),
      frequenciaUnidadeMedida: quotation.frequenciaUnidadeMedida || null,
      frequenciaUnidadeMedidaSigla: quotation.frequenciaUnidadeMedidaSigla || null,
      preco: toNumberOrNull(quotation.preco),
      cobrarSemUso: !!quotation.cobrarSemUso,
      condicaoLocacao: quotation.condicaoLocacao || null,
      precificacao: quotation.precificacao || origItem?.cotacao?.precificacao || item.precificacao,
      observacao: quotation.observacao?.trim() || null,
      precoReferencia: quotationCompatibility.precoReferencia ?? null,
      precoReferenciaCompativel: !!quotationCompatibility.precoReferenciaCompativel,
      tolerancias: quotation.tolerancias || [],
    },
  } as EventEquipmentType;
};

const buildEventVehicleFromProposal = (
  item: ProposalResiduePlanVehicleType | null,
  quotation: ProposalResiduePlanQuotationVehicleType | null,
  proposalQuotation: ProposalResiduePlanQuotationVehicleType | null,
  compra: boolean,
  origItem: EventVehicleType | null
): EventVehicleType | null => {
  if (!item || !quotation) {
    return null;
  }

  const quotationCompatibility = buildVehicleQuotationCompatibility(
    proposalQuotation,
    quotation,
    compra,
    origItem?.cotacao || null
  );

  return {
    ...buildFakeAuditObject(),
    ...(origItem || {}),
    id: item.id,
    idTemporaria: item.idTemporaria,
    idEventoVeiculo: origItem?.idEventoVeiculo || -1,
    action: origItem?.action || item.action || (origItem ? Action.Nothing : Action.Add),
    idEvento: origItem?.idEvento || null,
    idResiduoVeiculo: Number(item.idResiduoVeiculo),
    residuoVeiculo: item.residuoVeiculo,
    residuoVeiculoDescricao: item.residuoVeiculoDescricao,
    compartilhado: !!item.compartilhado,
    quantidade: Number(item.quantidade),
    quantidadeIdUnidadeMedida: Number(item.quantidadeIdUnidadeMedida),
    quantidadeUnidadeMedida: item.quantidadeUnidadeMedida,
    quantidadeUnidadeMedidaSigla: item.quantidadeUnidadeMedidaSigla,
    cargaMedia: Number(item.cargaMedia),
    cargaMediaIdUnidadeMedida: Number(item.cargaMediaIdUnidadeMedida),
    cargaMediaUnidadeMedida: item.cargaMediaUnidadeMedida,
    cargaMediaUnidadeMedidaSigla: item.cargaMediaUnidadeMedidaSigla,
    frota: origItem?.frota || null,
    placa: origItem?.placa || null,
    motorista: origItem?.motorista || null,
    rota: origItem?.rota || null,
    observacao: item.observacao?.trim() || null,
    acondicionamentos: origItem?.acondicionamentos || [],
    cotacao: {
      ...buildFakeAuditObject(),
      ...(origItem?.cotacao || {}),
      id: quotation.id,
      idTemporaria: quotation.idTemporaria,
      idEventoVeiculoCotacao: origItem?.cotacao?.idEventoVeiculoCotacao || -1,
      idEventoVeiculo: origItem?.idEventoVeiculo || -1,
      action:
        origItem?.cotacao?.action || quotation.action || (origItem?.cotacao ? Action.Nothing : Action.Add),
      idEntidadeResiduoVeiculo: toNumberOrNull(quotation.idEntidadeResiduoVeiculo),
      idResiduoVeiculo: Number(quotation.idResiduoVeiculo),
      residuoVeiculo: quotation.residuoVeiculo,
      residuoVeiculoDescricao: quotation.residuoVeiculoDescricao,
      idFornecedor: toNumberOrNull(quotation.idFornecedor),
      fornecedorCNPJ: quotation.fornecedorCNPJ,
      fornecedorRazaoSocial: quotation.fornecedorRazaoSocial,
      fornecedorNomeFantasia: quotation.fornecedorNomeFantasia,
      fornecedorCPF: quotation.fornecedorCPF,
      fornecedorNome: quotation.fornecedorNome,
      cobrarSeCompartilhado: !!quotation.cobrarSeCompartilhado,
      quantidade: toNumberOrNull(quotation.quantidade),
      quantidadeIdUnidadeMedida: toNumberOrNull(quotation.quantidadeIdUnidadeMedida),
      quantidadeUnidadeMedida: quotation.quantidadeUnidadeMedida || null,
      quantidadeUnidadeMedidaSigla: quotation.quantidadeUnidadeMedidaSigla || null,
      frequenciaIdUnidadeMedida: toNumberOrNull(quotation.frequenciaIdUnidadeMedida),
      frequenciaUnidadeMedida: quotation.frequenciaUnidadeMedida || null,
      frequenciaUnidadeMedidaSigla: quotation.frequenciaUnidadeMedidaSigla || null,
      minimoAceitavel: toNumberOrNull(quotation.minimoAceitavel),
      minimoAceitavelIdUnidadeMedida: toNumberOrNull(quotation.minimoAceitavelIdUnidadeMedida),
      minimoAceitavelUnidadeMedida: quotation.minimoAceitavelUnidadeMedida || null,
      minimoAceitavelUnidadeMedidaSigla: quotation.minimoAceitavelUnidadeMedidaSigla || null,
      preco: toNumberOrNull(quotation.preco),
      precificacao: quotation.precificacao || origItem?.cotacao?.precificacao || item.precificacao,
      observacao: quotation.observacao?.trim() || null,
      precoReferencia: quotationCompatibility.precoReferencia ?? null,
      precoReferenciaCompativel: !!quotationCompatibility.precoReferenciaCompativel,
      tolerancias: quotation.tolerancias || [],
    },
  } as EventVehicleType;
};

const buildEventTreatmentFromProposal = (
  item: ProposalResiduePlanTreatmentType | null,
  quotation: ProposalResiduePlanQuotationTreatmentType | null,
  proposalQuotation: ProposalResiduePlanQuotationTreatmentType | null,
  compra: boolean,
  origItem: EventTreatmentType | null
): EventTreatmentType | null => {
  if (!item || !quotation) {
    return null;
  }

  const quotationCompatibility = buildTreatmentQuotationCompatibility(
    proposalQuotation,
    quotation,
    compra,
    origItem?.cotacao || null
  );

  return {
    ...buildFakeAuditObject(),
    ...(origItem || {}),
    id: item.id,
    idTemporaria: item.idTemporaria,
    idEventoTratamento: origItem?.idEventoTratamento || -1,
    action: origItem?.action || item.action || (origItem ? Action.Nothing : Action.Add),
    idEvento: origItem?.idEvento || null,
    idResiduoTratamento: Number(item.idResiduoTratamento),
    residuoTratamento: item.residuoTratamento,
    residuoTratamentoDescricao: item.residuoTratamentoDescricao,
    autorizacaoAmbiental: !!item.autorizacaoAmbiental,
    observacao: item.observacao?.trim() || null,
    cotacao: {
      ...buildFakeAuditObject(),
      ...(origItem?.cotacao || {}),
      id: quotation.id,
      idTemporaria: quotation.idTemporaria,
      idEventoTratamentoCotacao: origItem?.cotacao?.idEventoTratamentoCotacao || -1,
      idEventoTratamento: origItem?.idEventoTratamento || -1,
      action:
        origItem?.cotacao?.action || quotation.action || (origItem?.cotacao ? Action.Nothing : Action.Add),
      idEntidadeResiduoTratamento: toNumberOrNull(quotation.idEntidadeResiduoTratamento),
      idResiduoTratamento: Number(quotation.idResiduoTratamento),
      residuoTratamento: quotation.residuoTratamento,
      residuoTratamentoDescricao: quotation.residuoTratamentoDescricao,
      idFornecedor: toNumberOrNull(quotation.idFornecedor),
      fornecedorCNPJ: quotation.fornecedorCNPJ,
      fornecedorRazaoSocial: quotation.fornecedorRazaoSocial,
      fornecedorNomeFantasia: quotation.fornecedorNomeFantasia,
      fornecedorCPF: quotation.fornecedorCPF,
      fornecedorNome: quotation.fornecedorNome,
      idEntidadeEndereco: toNumberOrNull(quotation.idEntidadeEndereco),
      idEntidade: toNumberOrNull(quotation.idEntidade),
      codigoEstado: quotation.codigoEstado,
      cidade: quotation.cidade,
      cidadeCodigoIBGE: toNumberOrNull(quotation.cidadeCodigoIBGE),
      bairro: quotation.bairro,
      logradouro: quotation.logradouro,
      numero: quotation.numero,
      complemento: quotation.complemento || null,
      referencia: quotation.referencia || null,
      cep: quotation.cep,
      latitude: toNumberOrNull(quotation.latitude),
      longitude: toNumberOrNull(quotation.longitude),
      quantidade: toNumberOrNull(quotation.quantidade),
      quantidadeIdUnidadeMedida: toNumberOrNull(quotation.quantidadeIdUnidadeMedida),
      quantidadeUnidadeMedida: quotation.quantidadeUnidadeMedida,
      quantidadeUnidadeMedidaSigla: quotation.quantidadeUnidadeMedidaSigla,
      minimoAceitavel: toNumberOrNull(quotation.minimoAceitavel),
      minimoAceitavelIdUnidadeMedida: toNumberOrNull(quotation.minimoAceitavelIdUnidadeMedida),
      minimoAceitavelUnidadeMedida: quotation.minimoAceitavelUnidadeMedida || null,
      minimoAceitavelUnidadeMedidaSigla: quotation.minimoAceitavelUnidadeMedidaSigla || null,
      preco: toNumberOrNull(quotation.preco),
      precificacao: quotation.precificacao || origItem?.cotacao?.precificacao || item.precificacao,
      observacao: quotation.observacao?.trim() || null,
      precoReferencia: quotationCompatibility.precoReferencia ?? null,
      precoReferenciaCompativel: !!quotationCompatibility.precoReferenciaCompativel,
      tolerancias: quotation.tolerancias || [],
    },
  } as EventTreatmentType;
};

const buildEventDestinationFromProposal = (
  item: ProposalResiduePlanDestinationType | null,
  quotation: ProposalResiduePlanQuotationDestinationType | null,
  proposalQuotation: ProposalResiduePlanQuotationDestinationType | null,
  compra: boolean,
  origItem: EventDestinationType | null
): EventDestinationType | null => {
  if (!item || !quotation) {
    return null;
  }

  const quotationCompatibility = buildDestinationQuotationCompatibility(
    proposalQuotation,
    quotation,
    compra,
    origItem?.cotacao || null
  );

  return {
    ...buildFakeAuditObject(),
    ...(origItem || {}),
    id: item.id,
    idTemporaria: item.idTemporaria,
    idEventoDestinoFinal: origItem?.idEventoDestinoFinal || -1,
    action: origItem?.action || item.action || (origItem ? Action.Nothing : Action.Add),
    idEvento: origItem?.idEvento || null,
    idResiduoDestinoFinal: Number(item.idResiduoDestinoFinal),
    residuoDestinoFinal: item.residuoDestinoFinal,
    residuoDestinoFinalDescricao: item.residuoDestinoFinalDescricao,
    autorizacaoAmbiental: !!item.autorizacaoAmbiental,
    observacao: item.observacao?.trim() || null,
    cotacao: {
      ...buildFakeAuditObject(),
      ...(origItem?.cotacao || {}),
      id: quotation.id,
      idTemporaria: quotation.idTemporaria,
      idEventoDestinoFinalCotacao: origItem?.cotacao?.idEventoDestinoFinalCotacao || -1,
      idEventoDestinoFinal: origItem?.idEventoDestinoFinal || -1,
      action:
        origItem?.cotacao?.action || quotation.action || (origItem?.cotacao ? Action.Nothing : Action.Add),
      idEntidadeResiduoDestinoFinal: toNumberOrNull(quotation.idEntidadeResiduoDestinoFinal),
      idResiduoDestinoFinal: Number(quotation.idResiduoDestinoFinal),
      residuoDestinoFinal: quotation.residuoDestinoFinal,
      residuoDestinoFinalDescricao: quotation.residuoDestinoFinalDescricao,
      idFornecedor: toNumberOrNull(quotation.idFornecedor),
      fornecedorCNPJ: quotation.fornecedorCNPJ,
      fornecedorRazaoSocial: quotation.fornecedorRazaoSocial,
      fornecedorNomeFantasia: quotation.fornecedorNomeFantasia,
      fornecedorCPF: quotation.fornecedorCPF,
      fornecedorNome: quotation.fornecedorNome,
      idEntidadeEndereco: toNumberOrNull(quotation.idEntidadeEndereco),
      idEntidade: toNumberOrNull(quotation.idEntidade),
      codigoEstado: quotation.codigoEstado,
      cidade: quotation.cidade,
      cidadeCodigoIBGE: toNumberOrNull(quotation.cidadeCodigoIBGE),
      bairro: quotation.bairro,
      logradouro: quotation.logradouro,
      numero: quotation.numero,
      complemento: quotation.complemento || null,
      referencia: quotation.referencia || null,
      cep: quotation.cep,
      latitude: toNumberOrNull(quotation.latitude),
      longitude: toNumberOrNull(quotation.longitude),
      quantidade: toNumberOrNull(quotation.quantidade),
      quantidadeIdUnidadeMedida: toNumberOrNull(quotation.quantidadeIdUnidadeMedida),
      quantidadeUnidadeMedida: quotation.quantidadeUnidadeMedida,
      quantidadeUnidadeMedidaSigla: quotation.quantidadeUnidadeMedidaSigla,
      minimoAceitavel: toNumberOrNull(quotation.minimoAceitavel),
      minimoAceitavelIdUnidadeMedida: toNumberOrNull(quotation.minimoAceitavelIdUnidadeMedida),
      minimoAceitavelUnidadeMedida: quotation.minimoAceitavelUnidadeMedida || null,
      minimoAceitavelUnidadeMedidaSigla: quotation.minimoAceitavelUnidadeMedidaSigla || null,
      preco: toNumberOrNull(quotation.preco),
      receita: !!quotation.receita,
      precificacao: quotation.precificacao || origItem?.cotacao?.precificacao || item.precificacao,
      observacao: quotation.observacao?.trim() || null,
      precoReferencia: quotationCompatibility.precoReferencia ?? null,
      precoReferenciaCompativel: !!quotationCompatibility.precoReferenciaCompativel,
      tolerancias: quotation.tolerancias || [],
    },
  } as EventDestinationType;
};

const buildProposalVehicleFromEvent = (
  item: EventVehicleType | null,
  plan: ProposalResiduePlanType | null
): ProposalResiduePlanVehicleType | null => {
  if (!item || !plan) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlano: plan.idPropostaResiduoPlano,
    idPropostaResiduoPlanoVeiculo:
      plan.veiculo?.idPropostaResiduoPlanoVeiculo || item.idEventoVeiculo || undefined,
  };
};

const buildProposalPackagingFromEvent = (
  item: EventPackagingType | null,
  plan: ProposalResiduePlanType | null
): ProposalResiduePlanPackagingType | null => {
  if (!item || !plan) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlano: plan.idPropostaResiduoPlano,
    idPropostaResiduoPlanoAcondicionamento:
      plan.acondicionamentos[0]?.idPropostaResiduoPlanoAcondicionamento ||
      item.idEventoAcondicionamento ||
      undefined,
  } as any;
};

const buildProposalEquipmentFromEvent = (
  item: EventEquipmentType | null,
  plan: ProposalResiduePlanType | null
): ProposalResiduePlanEquipmentType | null => {
  if (!item || !plan) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlano: plan.idPropostaResiduoPlano,
    idPropostaResiduoPlanoEquipamento:
      plan.equipamentos[0]?.idPropostaResiduoPlanoEquipamento || item.idEventoEquipamento || undefined,
  };
};

const buildProposalTreatmentFromEvent = (
  item: EventTreatmentType | null,
  plan: ProposalResiduePlanType | null
): ProposalResiduePlanTreatmentType | null => {
  if (!item || !plan) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlano: plan.idPropostaResiduoPlano,
    idPropostaResiduoPlanoTratamento:
      plan.tratamento?.idPropostaResiduoPlanoTratamento || item.idEventoTratamento || undefined,
  };
};

const buildProposalDestinationFromEvent = (
  item: EventDestinationType | null,
  plan: ProposalResiduePlanType | null
): ProposalResiduePlanDestinationType | null => {
  if (!item || !plan) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlano: plan.idPropostaResiduoPlano,
    idPropostaResiduoPlanoDestinoFinal:
      plan.destinoFinal?.idPropostaResiduoPlanoDestinoFinal || item.idEventoDestinoFinal || undefined,
  };
};

const buildProposalVehicleQuotationFromEvent = (
  item: EventVehicleQuotationType | null,
  quotation: ProposalResiduePlanQuotationType | null
): ProposalResiduePlanQuotationVehicleType | null => {
  if (!item || !quotation) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlanoCotacao: quotation.idPropostaResiduoPlanoCotacao,
    idPropostaResiduoPlanoCotacaoVeiculo:
      quotation.veiculo?.idPropostaResiduoPlanoCotacaoVeiculo || item.idEventoVeiculoCotacao || undefined,
    idPropostaResiduoPlanoVeiculo:
      quotation.veiculo?.idPropostaResiduoPlanoVeiculo || quotation.veiculo?.idPropostaResiduoPlanoVeiculo,
  } as any;
};

const buildProposalPackagingQuotationFromEvent = (
  item: EventPackagingQuotationType | null,
  quotation: ProposalResiduePlanQuotationType | null
): ProposalResiduePlanQuotationPackagingType | null => {
  if (!item || !quotation) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlanoCotacao: quotation.idPropostaResiduoPlanoCotacao,
    idPropostaResiduoPlanoCotacaoAcondicionamento:
      quotation.acondicionamentos[0]?.idPropostaResiduoPlanoCotacaoAcondicionamento ||
      item.idEventoAcondicionamentoCotacao ||
      undefined,
    idPropostaResiduoPlanoAcondicionamento:
      quotation.acondicionamentos[0]?.idPropostaResiduoPlanoAcondicionamento || undefined,
  } as any;
};

const buildProposalEquipmentQuotationFromEvent = (
  item: EventEquipmentQuotationType | null,
  quotation: ProposalResiduePlanQuotationType | null
): ProposalResiduePlanQuotationEquipmentType | null => {
  if (!item || !quotation) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlanoCotacao: quotation.idPropostaResiduoPlanoCotacao,
    idPropostaResiduoPlanoCotacaoEquipamento:
      quotation.equipamentos[0]?.idPropostaResiduoPlanoCotacaoEquipamento ||
      item.idEventoEquipamentoCotacao ||
      undefined,
    idPropostaResiduoPlanoEquipamento:
      quotation.equipamentos[0]?.idPropostaResiduoPlanoEquipamento || undefined,
  } as any;
};

const buildProposalTreatmentQuotationFromEvent = (
  item: EventTreatmentQuotationType | null,
  quotation: ProposalResiduePlanQuotationType | null
): ProposalResiduePlanQuotationTreatmentType | null => {
  if (!item || !quotation) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlanoCotacao: quotation.idPropostaResiduoPlanoCotacao,
    idPropostaResiduoPlanoCotacaoTratamento:
      quotation.tratamento?.idPropostaResiduoPlanoCotacaoTratamento ||
      item.idEventoTratamentoCotacao ||
      undefined,
    idPropostaResiduoPlanoTratamento: quotation.tratamento?.idPropostaResiduoPlanoTratamento || undefined,
  } as any;
};

const buildProposalDestinationQuotationFromEvent = (
  item: EventDestinationQuotationType | null,
  quotation: ProposalResiduePlanQuotationType | null
): ProposalResiduePlanQuotationDestinationType | null => {
  if (!item || !quotation) {
    return null;
  }

  return {
    ...item,
    idPropostaResiduoPlanoCotacao: quotation.idPropostaResiduoPlanoCotacao,
    idPropostaResiduoPlanoCotacaoDestinoFinal:
      quotation.destinoFinal?.idPropostaResiduoPlanoCotacaoDestinoFinal ||
      item.idEventoDestinoFinalCotacao ||
      undefined,
    idPropostaResiduoPlanoDestinoFinal:
      quotation.destinoFinal?.idPropostaResiduoPlanoDestinoFinal || undefined,
  } as any;
};

const buildDeletedEventVehicle = (origItem: EventVehicleType | null): EventVehicleType | null => {
  if (!origItem) {
    return null;
  }

  return {
    ...origItem,
    action: Action.Delete,
    cotacao: origItem.cotacao
      ? {
          ...origItem.cotacao,
          action: Action.Delete,
        }
      : origItem.cotacao,
  };
};

const buildDeletedEventPackaging = (origItem: EventPackagingType | null): EventPackagingType | null => {
  if (!origItem) {
    return null;
  }

  return {
    ...origItem,
    action: Action.Delete,
    cotacao: origItem.cotacao
      ? {
          ...origItem.cotacao,
          action: Action.Delete,
        }
      : origItem.cotacao,
  };
};

const buildDeletedEventEquipment = (origItem: EventEquipmentType | null): EventEquipmentType | null => {
  if (!origItem) {
    return null;
  }

  return {
    ...origItem,
    action: Action.Delete,
    cotacao: origItem.cotacao
      ? {
          ...origItem.cotacao,
          action: Action.Delete,
        }
      : origItem.cotacao,
  };
};

const buildDeletedEventTreatment = (origItem: EventTreatmentType | null): EventTreatmentType | null => {
  if (!origItem) {
    return null;
  }

  return {
    ...origItem,
    action: Action.Delete,
    cotacao: origItem.cotacao
      ? {
          ...origItem.cotacao,
          action: Action.Delete,
        }
      : origItem.cotacao,
  };
};

const buildDeletedEventDestination = (origItem: EventDestinationType | null): EventDestinationType | null => {
  if (!origItem) {
    return null;
  }

  return {
    ...origItem,
    action: Action.Delete,
    cotacao: origItem.cotacao
      ? {
          ...origItem.cotacao,
          action: Action.Delete,
        }
      : origItem.cotacao,
  };
};

const buildDeletedEventVehicleQuotation = (origItem: EventVehicleType | null): EventVehicleType | null => {
  if (!origItem?.cotacao) {
    return null;
  }

  return {
    ...origItem,
    action: origItem.action || Action.Nothing,
    cotacao: {
      ...origItem.cotacao,
      action: Action.Delete,
    },
  };
};

const buildDeletedEventPackagingQuotation = (
  origItem: EventPackagingType | null
): EventPackagingType | null => {
  if (!origItem?.cotacao) {
    return null;
  }

  return {
    ...origItem,
    action: origItem.action || Action.Nothing,
    cotacao: {
      ...origItem.cotacao,
      action: Action.Delete,
    },
  };
};

const buildDeletedEventEquipmentQuotation = (
  origItem: EventEquipmentType | null
): EventEquipmentType | null => {
  if (!origItem?.cotacao) {
    return null;
  }

  return {
    ...origItem,
    action: origItem.action || Action.Nothing,
    cotacao: {
      ...origItem.cotacao,
      action: Action.Delete,
    },
  };
};

const buildDeletedEventTreatmentQuotation = (
  origItem: EventTreatmentType | null
): EventTreatmentType | null => {
  if (!origItem?.cotacao) {
    return null;
  }

  return {
    ...origItem,
    action: origItem.action || Action.Nothing,
    cotacao: {
      ...origItem.cotacao,
      action: Action.Delete,
    },
  };
};

const buildDeletedEventDestinationQuotation = (
  origItem: EventDestinationType | null
): EventDestinationType | null => {
  if (!origItem?.cotacao) {
    return null;
  }

  return {
    ...origItem,
    action: origItem.action || Action.Nothing,
    cotacao: {
      ...origItem.cotacao,
      action: Action.Delete,
    },
  };
};

const EventResidueReferenceEditor = forwardRef((props: EventResidueReferenceEditorProps, ref) => {
  const contextValue = useEventGeneralContext();
  const eventGeneralData = contextValue?.eventGeneralData;
  const setEventGeneralData = contextValue?.setEventGeneralData;
  const currentReferencia =
    eventGeneralData?.codigoEventoReferencia || props.event?.codigoEventoReferencia || null;
  const currentTipo = eventGeneralData?.codigoEventoTipo || props.event?.codigoEventoTipo || null;
  const currentReferencia2 =
    eventGeneralData?.idEventoReferencia2 ?? props.event?.idEventoReferencia2 ?? null;
  const eventoReferenciaPersonalizado = !!eventGeneralData?.eventoReferenciaPersonalizado;
  const modoEdicao =
    !props.disabled &&
    !props.statusSoftDisabled &&
    (eventoReferenciaPersonalizado ||
      !!props.event?.personalizado ||
      props.event?.idEventoReferencia2 === null);
  const isPackagingReference = currentReferencia === ReferenciaCodigo.ContratoResiduoAcondicionamento;
  const isEquipmentReference = currentReferencia === ReferenciaCodigo.ContratoResiduoEquipamento;
  const isMobilizationEvent =
    props.event?.codigoEventoTipo === TipoCodigo.MobilizacaoEnviar ||
    props.event?.codigoEventoTipo === TipoCodigo.MobilizacaoRetirar ||
    currentTipo === TipoCodigo.MobilizacaoEnviar ||
    currentTipo === TipoCodigo.MobilizacaoRetirar;
  const shouldForceSingleCustomFlow = !!props.forceSingleCustomFlow;
  const shouldHideCustomSectionSelectors =
    shouldForceSingleCustomFlow && (isMobilizationEvent || isPackagingReference || isEquipmentReference);
  const isCustomCollectionFlow =
    shouldForceSingleCustomFlow && !isMobilizationEvent && !isPackagingReference && !isEquipmentReference;
  const isEditableReferenceFlow =
    modoEdicao &&
    (shouldForceSingleCustomFlow ||
      !!props.event?.personalizado ||
      props.event?.idEventoReferencia2 === null);
  const shouldFilterDisplayByEventPresence = !!props.event && !isEditableReferenceFlow;
  const defaultCustomVehicleSection = shouldForceSingleCustomFlow ? isCustomCollectionFlow : undefined;
  const defaultCustomDestinationSection = shouldForceSingleCustomFlow ? isCustomCollectionFlow : undefined;
  const selectedPlan = useMemo(() => {
    if (!props.residue) {
      return null;
    }

    if (shouldForceSingleCustomFlow) {
      const customPlanCode =
        isPackagingReference || isEquipmentReference
          ? ProposalResiduePlanCodeEnum.Mobilization
          : ProposalResiduePlanCodeEnum.Regular;
      const selectedPlanById = props.selectedPlanId ? findPlan(props.residue, props.selectedPlanId) : null;
      const selectedPlanByCode =
        props.residue.planos.find(
          (plan) => (plan.codigoPlanoTipo || ProposalResiduePlanCodeEnum.Mobilization) === customPlanCode
        ) || null;

      return (
        (selectedPlanById &&
        (selectedPlanById.codigoPlanoTipo || ProposalResiduePlanCodeEnum.Mobilization) === customPlanCode
          ? selectedPlanById
          : null) ||
        selectedPlanByCode ||
        buildEmptyPlan(props.residue, customPlanCode)
      );
    }

    if (isPackagingReference) {
      return (
        props.residue.planos.find(
          (plan) =>
            plan.acondicionamentos.findIndex(
              (item) => Number(item.idPropostaResiduoPlanoAcondicionamento) === Number(currentReferencia2)
            ) > -1
        ) ||
        props.residue.planos.find(
          (plan) =>
            plan.acondicionamentos.findIndex(
              (item) =>
                Number(item.idResiduoAcondicionamento) ===
                Number(props.event?.acondicionamento?.idResiduoAcondicionamento)
            ) > -1
        ) ||
        findPlan(props.residue, props.selectedPlanId) ||
        null
      );
    }

    if (isEquipmentReference) {
      return (
        props.residue.planos.find(
          (plan) =>
            plan.equipamentos.findIndex(
              (item) => Number(item.idPropostaResiduoPlanoEquipamento) === Number(currentReferencia2)
            ) > -1
        ) ||
        props.residue.planos.find(
          (plan) =>
            plan.equipamentos.findIndex(
              (item) =>
                Number(item.idResiduoEquipamento) === Number(props.event?.equipamento?.idResiduoEquipamento)
            ) > -1
        ) ||
        findPlan(props.residue, props.selectedPlanId) ||
        null
      );
    }

    return findPlan(props.residue, props.selectedPlanId);
  }, [
    currentReferencia2,
    isEquipmentReference,
    isPackagingReference,
    props.event?.acondicionamento?.idResiduoAcondicionamento,
    props.event?.equipamento?.idResiduoEquipamento,
    props.residue,
    props.selectedPlanId,
    shouldForceSingleCustomFlow,
  ]);

  const [quotationId, setQuotationId] = useState<number | null>(null);
  const [autoImportedMissingSupplier, setAutoImportedMissingSupplier] = useState(false);

  const [packagingPlanItem, setPackagingPlanItem] = useState<
    ProposalResiduePlanPackagingType | null | undefined
  >(undefined);
  const [equipmentPlanItem, setEquipmentPlanItem] = useState<
    ProposalResiduePlanEquipmentType | null | undefined
  >(undefined);

  const [vehiclePlanItem, setVehiclePlanItem] = useState<ProposalResiduePlanVehicleType | null | undefined>(
    undefined
  );
  const [treatmentPlanItem, setTreatmentPlanItem] = useState<
    ProposalResiduePlanTreatmentType | null | undefined
  >(undefined);
  const [destinationPlanItem, setDestinationPlanItem] = useState<
    ProposalResiduePlanDestinationType | null | undefined
  >(undefined);

  const [packagingQuotationItem, setPackagingQuotationItem] = useState<
    ProposalResiduePlanQuotationPackagingType | null | undefined
  >(undefined);
  const [equipmentQuotationItem, setEquipmentQuotationItem] = useState<
    ProposalResiduePlanQuotationEquipmentType | null | undefined
  >(undefined);
  const [vehicleQuotationItem, setVehicleQuotationItem] = useState<
    ProposalResiduePlanQuotationVehicleType | null | undefined
  >(undefined);
  const [treatmentQuotationItem, setTreatmentQuotationItem] = useState<
    ProposalResiduePlanQuotationTreatmentType | null | undefined
  >(undefined);
  const [destinationQuotationItem, setDestinationQuotationItem] = useState<
    ProposalResiduePlanQuotationDestinationType | null | undefined
  >(undefined);

  useEffect(() => {
    if (!selectedPlan) {
      setQuotationId(null);
      return;
    }

    if (shouldForceSingleCustomFlow) {
      const currentQuotationStillExists = selectedPlan.cotacoes.some(
        (quotation) => Number(quotation.idPropostaResiduoPlanoCotacao) === Number(quotationId)
      );

      setQuotationId(
        props.selectedQuotationId ||
          (currentQuotationStillExists
            ? quotationId
            : props.event?.idEventoReferencia3 ||
              selectedPlan.cotacoes.find((x) => x.primario)?.idPropostaResiduoPlanoCotacao ||
              selectedPlan.cotacoes[0]?.idPropostaResiduoPlanoCotacao ||
              -1)
      );
      return;
    }

    if (isPackagingReference) {
      const selectedQuotationByItem =
        selectedPlan.cotacoes.find((quotation) =>
          quotation.acondicionamentos.some(
            (item) =>
              Number(item.idPropostaResiduoPlanoCotacaoAcondicionamento) ===
              Number(props.event?.idEventoReferencia3)
          )
        ) || null;

      setQuotationId(
        props.selectedQuotationId ||
          selectedQuotationByItem?.idPropostaResiduoPlanoCotacao ||
          selectedPlan.cotacoes.find((quotation) =>
            quotation.acondicionamentos.some(
              (item) => Number(item.idPropostaResiduoPlanoAcondicionamento) === Number(currentReferencia2)
            )
          )?.idPropostaResiduoPlanoCotacao ||
          selectedPlan.cotacoes.find((x) => x.primario)?.idPropostaResiduoPlanoCotacao ||
          selectedPlan.cotacoes[0]?.idPropostaResiduoPlanoCotacao ||
          null
      );

      return;
    }

    if (isEquipmentReference) {
      const selectedQuotationByItem =
        selectedPlan.cotacoes.find((quotation) =>
          quotation.equipamentos.some(
            (item) =>
              Number(item.idPropostaResiduoPlanoCotacaoEquipamento) ===
              Number(props.event?.idEventoReferencia3)
          )
        ) || null;

      setQuotationId(
        props.selectedQuotationId ||
          selectedQuotationByItem?.idPropostaResiduoPlanoCotacao ||
          selectedPlan.cotacoes.find((quotation) =>
            quotation.equipamentos.some(
              (item) => Number(item.idPropostaResiduoPlanoEquipamento) === Number(currentReferencia2)
            )
          )?.idPropostaResiduoPlanoCotacao ||
          selectedPlan.cotacoes.find((x) => x.primario)?.idPropostaResiduoPlanoCotacao ||
          selectedPlan.cotacoes[0]?.idPropostaResiduoPlanoCotacao ||
          null
      );

      return;
    }

    const selectedQuotationByEventItem =
      selectedPlan.cotacoes.find(
        (quotation) =>
          Number(quotation.veiculo?.idPropostaResiduoPlanoCotacaoVeiculo) ===
            Number(props.event?.veiculo?.cotacao?.idEventoVeiculoCotacao) ||
          Number(quotation.tratamento?.idPropostaResiduoPlanoCotacaoTratamento) ===
            Number(props.event?.tratamento?.cotacao?.idEventoTratamentoCotacao) ||
          Number(quotation.destinoFinal?.idPropostaResiduoPlanoCotacaoDestinoFinal) ===
            Number(props.event?.destinoFinal?.cotacao?.idEventoDestinoFinalCotacao)
      ) || null;

    setQuotationId(
      props.selectedQuotationId ||
        props.event?.idEventoReferencia3 ||
        selectedQuotationByEventItem?.idPropostaResiduoPlanoCotacao ||
        selectedPlan.cotacoes.find((x) => x.primario)?.idPropostaResiduoPlanoCotacao ||
        selectedPlan.cotacoes[0]?.idPropostaResiduoPlanoCotacao ||
        null
    );
  }, [
    currentReferencia2,
    isEquipmentReference,
    isPackagingReference,
    props.event?.destinoFinal?.cotacao?.idEventoDestinoFinalCotacao,
    props.event?.idEventoReferencia3,
    props.event?.tratamento?.cotacao?.idEventoTratamentoCotacao,
    props.event?.veiculo?.cotacao?.idEventoVeiculoCotacao,
    props.selectedQuotationId,
    quotationId,
    selectedPlan,
    shouldForceSingleCustomFlow,
  ]);

  useEffect(() => {
    setPackagingPlanItem(undefined);
    setEquipmentPlanItem(undefined);
    setVehiclePlanItem(undefined);
    setTreatmentPlanItem(undefined);
    setDestinationPlanItem(undefined);
    setPackagingQuotationItem(undefined);
    setEquipmentQuotationItem(undefined);
    setVehicleQuotationItem(undefined);
    setTreatmentQuotationItem(undefined);
    setDestinationQuotationItem(undefined);
  }, [quotationId, props.event?.idEvento]);

  const selectedQuotation =
    selectedPlan?.cotacoes.find((x) => Number(x.idPropostaResiduoPlanoCotacao) === Number(quotationId)) ||
    null;
  const quotationBase =
    selectedQuotation || selectedPlan?.cotacoes.find((x) => x.primario) || selectedPlan?.cotacoes[0] || null;

  const defaultPackagingPlanItem =
    buildProposalPackagingFromEvent(props.event?.acondicionamento || null, selectedPlan) ||
    selectedPlan?.acondicionamentos.find(
      (item) => Number(item.idPropostaResiduoPlanoAcondicionamento) === Number(currentReferencia2)
    ) ||
    selectedPlan?.acondicionamentos[0] ||
    null;
  const defaultEquipmentPlanItem =
    buildProposalEquipmentFromEvent(props.event?.equipamento || null, selectedPlan) ||
    selectedPlan?.equipamentos.find(
      (item) => Number(item.idPropostaResiduoPlanoEquipamento) === Number(currentReferencia2)
    ) ||
    selectedPlan?.equipamentos[0] ||
    null;
  const resolvedVehiclePlanItem =
    vehiclePlanItem !== undefined
      ? vehiclePlanItem
      : buildProposalVehicleFromEvent(props.event?.veiculo || null, selectedPlan) ||
        selectedPlan?.veiculo ||
        null;
  const resolvedPackagingPlanItem =
    packagingPlanItem !== undefined ? packagingPlanItem : defaultPackagingPlanItem;
  const resolvedEquipmentPlanItem =
    equipmentPlanItem !== undefined ? equipmentPlanItem : defaultEquipmentPlanItem;
  const resolvedTreatmentPlanItem =
    treatmentPlanItem !== undefined
      ? treatmentPlanItem
      : buildProposalTreatmentFromEvent(props.event?.tratamento || null, selectedPlan) ||
        selectedPlan?.tratamento ||
        null;
  const resolvedDestinationPlanItem =
    destinationPlanItem !== undefined
      ? destinationPlanItem
      : buildProposalDestinationFromEvent(props.event?.destinoFinal || null, selectedPlan) ||
        selectedPlan?.destinoFinal ||
        null;

  const resolvedVehicleQuotationItem =
    vehicleQuotationItem !== undefined
      ? vehicleQuotationItem
      : buildProposalVehicleQuotationFromEvent(props.event?.veiculo?.cotacao || null, quotationBase) ||
        quotationBase?.veiculo ||
        null;
  const resolvedPackagingQuotationItem =
    packagingQuotationItem !== undefined
      ? packagingQuotationItem
      : buildProposalPackagingQuotationFromEvent(
          props.event?.acondicionamento?.cotacao || null,
          quotationBase
        ) ||
        quotationBase?.acondicionamentos.find(
          (item) =>
            Number(item.idPropostaResiduoPlanoAcondicionamento) ===
            Number(resolvedPackagingPlanItem?.idPropostaResiduoPlanoAcondicionamento)
        ) ||
        null;
  const resolvedEquipmentQuotationItem =
    equipmentQuotationItem !== undefined
      ? equipmentQuotationItem
      : buildProposalEquipmentQuotationFromEvent(props.event?.equipamento?.cotacao || null, quotationBase) ||
        quotationBase?.equipamentos.find(
          (item) =>
            Number(item.idPropostaResiduoPlanoEquipamento) ===
            Number(resolvedEquipmentPlanItem?.idPropostaResiduoPlanoEquipamento)
        ) ||
        null;
  const resolvedTreatmentQuotationItem =
    treatmentQuotationItem !== undefined
      ? treatmentQuotationItem
      : buildProposalTreatmentQuotationFromEvent(props.event?.tratamento?.cotacao || null, quotationBase) ||
        quotationBase?.tratamento ||
        null;
  const resolvedDestinationQuotationItem =
    destinationQuotationItem !== undefined
      ? destinationQuotationItem
      : buildProposalDestinationQuotationFromEvent(
          props.event?.destinoFinal?.cotacao || null,
          quotationBase
        ) ||
        quotationBase?.destinoFinal ||
        null;

  const resolveDisplayItem = <T,>(item: T | null, shouldDisplay: boolean) => {
    if (!shouldFilterDisplayByEventPresence) {
      return item;
    }

    return shouldDisplay ? item : null;
  };

  const displayPackagingPlanItem = resolveDisplayItem(
    resolvedPackagingPlanItem,
    !!props.event?.acondicionamento
  );
  const displayEquipmentPlanItem = resolveDisplayItem(resolvedEquipmentPlanItem, !!props.event?.equipamento);
  const displayVehiclePlanItem = resolveDisplayItem(resolvedVehiclePlanItem, !!props.event?.veiculo);
  const displayTreatmentPlanItem = resolveDisplayItem(resolvedTreatmentPlanItem, !!props.event?.tratamento);
  const displayDestinationPlanItem = resolveDisplayItem(
    resolvedDestinationPlanItem,
    !!props.event?.destinoFinal
  );

  const displayPackagingQuotationItem = resolveDisplayItem(
    resolvedPackagingQuotationItem,
    !!props.event?.acondicionamento
  );
  const displayEquipmentQuotationItem = resolveDisplayItem(
    resolvedEquipmentQuotationItem,
    !!props.event?.equipamento
  );
  const displayVehicleQuotationItem = resolveDisplayItem(
    resolvedVehicleQuotationItem,
    !!props.event?.veiculo
  );
  const displayTreatmentQuotationItem = resolveDisplayItem(
    resolvedTreatmentQuotationItem,
    !!props.event?.tratamento
  );
  const displayDestinationQuotationItem = resolveDisplayItem(
    resolvedDestinationQuotationItem,
    !!props.event?.destinoFinal
  );

  useEffect(() => {
    const isResidueReference =
      currentReferencia === ReferenciaCodigo.ContratoResiduoAcondicionamento ||
      currentReferencia === ReferenciaCodigo.ContratoResiduoEquipamento ||
      currentReferencia === ReferenciaCodigo.ContratoResiduoPlano;
    const isSupportedEventType =
      currentTipo === TipoCodigo.Coleta ||
      currentTipo === TipoCodigo.MobilizacaoEnviar ||
      currentTipo === TipoCodigo.MobilizacaoRetirar;

    if (!isResidueReference || !isSupportedEventType || !props.residue) {
      setAutoImportedMissingSupplier(false);
      return;
    }

    let hasMissingSupplier = false;

    if (isPackagingReference) {
      hasMissingSupplier = !!resolvedPackagingQuotationItem && !resolvedPackagingQuotationItem.idFornecedor;
    } else if (isEquipmentReference) {
      hasMissingSupplier = !!resolvedEquipmentQuotationItem && !resolvedEquipmentQuotationItem.idFornecedor;
    } else {
      hasMissingSupplier = !!(
        (resolvedVehicleQuotationItem && !resolvedVehicleQuotationItem.idFornecedor) ||
        (resolvedTreatmentQuotationItem && !resolvedTreatmentQuotationItem.idFornecedor) ||
        (resolvedDestinationQuotationItem && !resolvedDestinationQuotationItem.idFornecedor)
      );
    }

    setAutoImportedMissingSupplier(hasMissingSupplier);

    if (
      hasMissingSupplier &&
      !eventoReferenciaPersonalizado &&
      !props.disabled &&
      !props.statusSoftDisabled &&
      setEventGeneralData &&
      eventGeneralData
    ) {
      setEventGeneralData({
        ...eventGeneralData,
        eventoReferenciaPersonalizado: true,
      });
    }
  }, [
    currentReferencia,
    currentTipo,
    eventGeneralData,
    eventoReferenciaPersonalizado,
    isEquipmentReference,
    isPackagingReference,
    props.disabled,
    props.residue,
    props.statusSoftDisabled,
    resolvedDestinationQuotationItem,
    resolvedEquipmentQuotationItem,
    resolvedPackagingQuotationItem,
    resolvedTreatmentQuotationItem,
    resolvedVehicleQuotationItem,
    setEventGeneralData,
  ]);

  const summaryResidue = useMemo(() => {
    if (!props.residue || !selectedPlan) {
      return null;
    }

    const summaryPackagingQuotation =
      packagingQuotationItem !== undefined ? packagingQuotationItem : displayPackagingQuotationItem;
    const summaryEquipmentQuotation =
      equipmentQuotationItem !== undefined ? equipmentQuotationItem : displayEquipmentQuotationItem;
    const summaryVehicleQuotation =
      vehicleQuotationItem !== undefined ? vehicleQuotationItem : displayVehicleQuotationItem;
    const summaryTreatmentQuotation =
      treatmentQuotationItem !== undefined ? treatmentQuotationItem : displayTreatmentQuotationItem;
    const summaryDestinationQuotation =
      destinationQuotationItem !== undefined ? destinationQuotationItem : displayDestinationQuotationItem;

    const quotation: ProposalResiduePlanQuotationType | null = quotationBase
      ? {
          ...quotationBase,
          acondicionamentos: summaryPackagingQuotation ? [summaryPackagingQuotation] : [],
          equipamentos: summaryEquipmentQuotation ? [summaryEquipmentQuotation] : [],
          veiculo: summaryVehicleQuotation as any,
          tratamento: summaryTreatmentQuotation as any,
          destinoFinal: summaryDestinationQuotation as any,
        }
      : null;

    const plan: ProposalResiduePlanType = {
      ...selectedPlan,
      codigoPlanoTipo:
        selectedPlan.codigoPlanoTipo ||
        (isPackagingReference || isEquipmentReference
          ? ProposalResiduePlanCodeEnum.Mobilization
          : ProposalResiduePlanCodeEnum.Regular),
      primario: true,
      acondicionamentos: displayPackagingPlanItem ? [displayPackagingPlanItem] : [],
      equipamentos: displayEquipmentPlanItem ? [displayEquipmentPlanItem] : [],
      veiculo: displayVehiclePlanItem,
      tratamento: displayTreatmentPlanItem,
      destinoFinal: displayDestinationPlanItem,
      cotacoes: quotation ? [{ ...quotation, primario: true }] : [],
    };

    return {
      ...props.residue,
      planos: [plan],
    };
  }, [
    equipmentQuotationItem,
    isEquipmentReference,
    isPackagingReference,
    packagingQuotationItem,
    destinationQuotationItem,
    displayDestinationPlanItem,
    displayDestinationQuotationItem,
    displayEquipmentPlanItem,
    displayEquipmentQuotationItem,
    displayPackagingPlanItem,
    displayPackagingQuotationItem,
    displayTreatmentPlanItem,
    displayTreatmentQuotationItem,
    displayVehiclePlanItem,
    displayVehicleQuotationItem,
    props.residue,
    selectedPlan,
    quotationBase,
    treatmentQuotationItem,
    vehicleQuotationItem,
  ]);

  useImperativeHandle(ref, () => ({
    validate() {
      if (!props.residue) {
        throw Error('Resíduo não encontrado.');
      }

      const { residue } = props;
      const response: Record<string, unknown> = {};

      let acondicionamento = null;
      if (packagingPlanItem === null) {
        acondicionamento = buildDeletedEventPackaging(props.event?.acondicionamento || null);
      } else if (packagingQuotationItem === null) {
        acondicionamento = buildDeletedEventPackagingQuotation(props.event?.acondicionamento || null);
      } else {
        acondicionamento = buildEventPackagingFromProposal(
          resolvedPackagingPlanItem,
          resolvedPackagingQuotationItem,
          selectedQuotation?.acondicionamentos.find(
            (item) =>
              Number(item.idPropostaResiduoPlanoAcondicionamento) ===
              Number(resolvedPackagingPlanItem?.idPropostaResiduoPlanoAcondicionamento)
          ) || null,
          residue.compra,
          props.event?.acondicionamento || null
        );
      }

      let equipamento = null;
      if (equipmentPlanItem === null) {
        equipamento = buildDeletedEventEquipment(props.event?.equipamento || null);
      } else if (equipmentQuotationItem === null) {
        equipamento = buildDeletedEventEquipmentQuotation(props.event?.equipamento || null);
      } else {
        equipamento = buildEventEquipmentFromProposal(
          resolvedEquipmentPlanItem,
          resolvedEquipmentQuotationItem,
          selectedQuotation?.equipamentos.find(
            (item) =>
              Number(item.idPropostaResiduoPlanoEquipamento) ===
              Number(resolvedEquipmentPlanItem?.idPropostaResiduoPlanoEquipamento)
          ) || null,
          residue.compra,
          props.event?.equipamento || null
        );
      }

      let veiculo = null;
      if (vehiclePlanItem === null) {
        veiculo = buildDeletedEventVehicle(props.event?.veiculo || null);
      } else if (vehicleQuotationItem === null) {
        veiculo = buildDeletedEventVehicleQuotation(props.event?.veiculo || null);
      } else {
        veiculo = buildEventVehicleFromProposal(
          resolvedVehiclePlanItem,
          resolvedVehicleQuotationItem,
          selectedQuotation?.veiculo || null,
          residue.compra,
          props.event?.veiculo || null
        );
      }

      let tratamento = null;
      if (treatmentPlanItem === null) {
        tratamento = buildDeletedEventTreatment(props.event?.tratamento || null);
      } else if (treatmentQuotationItem === null) {
        tratamento = buildDeletedEventTreatmentQuotation(props.event?.tratamento || null);
      } else {
        tratamento = buildEventTreatmentFromProposal(
          resolvedTreatmentPlanItem,
          resolvedTreatmentQuotationItem,
          selectedQuotation?.tratamento || null,
          residue.compra,
          props.event?.tratamento || null
        );
      }

      let destinoFinal = null;
      if (destinationPlanItem === null) {
        destinoFinal = buildDeletedEventDestination(props.event?.destinoFinal || null);
      } else if (destinationQuotationItem === null) {
        destinoFinal = buildDeletedEventDestinationQuotation(props.event?.destinoFinal || null);
      } else {
        destinoFinal = buildEventDestinationFromProposal(
          resolvedDestinationPlanItem,
          resolvedDestinationQuotationItem,
          selectedQuotation?.destinoFinal || null,
          residue.compra,
          props.event?.destinoFinal || null
        );
      }

      if (isPackagingReference) {
        if (!acondicionamento) {
          throw Error('Acondicionamento deve ser preenchido.');
        }
        if (!acondicionamento.cotacao?.idFornecedor) {
          throw Error('Fornecedor deve ser preenchido para o acondicionamento.');
        }

        response.acondicionamento = acondicionamento;
        response.idEventoReferencia3 =
          resolvedPackagingQuotationItem?.idPropostaResiduoPlanoCotacaoAcondicionamento || null;
        return response;
      }

      if (isEquipmentReference) {
        if (!equipamento) {
          throw Error('Equipamento deve ser preenchido.');
        }
        if (!equipamento.cotacao?.idFornecedor) {
          throw Error('Fornecedor deve ser preenchido para o equipamento.');
        }

        response.equipamento = equipamento;
        response.idEventoReferencia3 =
          resolvedEquipmentQuotationItem?.idPropostaResiduoPlanoCotacaoEquipamento || null;
        return response;
      }

      if (veiculo) {
        if (!veiculo.cotacao?.idFornecedor) {
          throw Error('Fornecedor deve ser preenchido para o veículo.');
        }
        response.veiculo = veiculo;
      }

      if (tratamento) {
        if (!tratamento.cotacao?.idFornecedor) {
          throw Error('Fornecedor deve ser preenchido para o tratamento.');
        }
        response.tratamento = tratamento;
      }

      if (destinoFinal) {
        if (!destinoFinal.cotacao?.idFornecedor) {
          throw Error('Fornecedor deve ser preenchido para o destino final.');
        }
        response.destinoFinal = destinoFinal;
      }

      if (!veiculo && !tratamento && !destinoFinal) {
        throw Error('Veículo, Tratamento ou Destino Final deve ser preenchido.');
      }

      response.idEventoReferencia3 = quotationId || null;

      return response;
    },
  }));

  if (!props.residue || !selectedPlan || !summaryResidue) {
    return null;
  }

  const quotationOptions = selectedPlan.cotacoes
    .sort((a, b) => Number(a.primario ? 0 : 1) - Number(b.primario ? 0 : 1))
    .map((item) => ({
      label: `Cotação #${item.idPropostaResiduoPlanoCotacao}`,
      value: item.idPropostaResiduoPlanoCotacao!.toString(),
      group: `Cotação ${item.primario ? 'Primária' : 'Alternativa'}`,
      extras: [
        item.acondicionamentos[0]
          ? {
              icone: Feature.Reference.Packaging.icon,
              fornecedor:
                item.acondicionamentos[0].fornecedorNomeFantasia ||
                item.acondicionamentos[0].fornecedorRazaoSocial ||
                item.acondicionamentos[0].fornecedorNome ||
                '-',
            }
          : undefined,
        item.equipamentos[0]
          ? {
              icone: Feature.Reference.Equipment.icon,
              fornecedor:
                item.equipamentos[0].fornecedorNomeFantasia ||
                item.equipamentos[0].fornecedorRazaoSocial ||
                item.equipamentos[0].fornecedorNome ||
                '-',
            }
          : undefined,
        item.veiculo
          ? {
              icone: Feature.Reference.Vehicle.icon,
              fornecedor:
                item.veiculo.fornecedorNomeFantasia ||
                item.veiculo.fornecedorRazaoSocial ||
                item.veiculo.fornecedorNome ||
                '-',
            }
          : undefined,
        item.tratamento
          ? {
              icone: Feature.Reference.Treatment.icon,
              fornecedor:
                item.tratamento.fornecedorNomeFantasia ||
                item.tratamento.fornecedorRazaoSocial ||
                item.tratamento.fornecedorNome ||
                '-',
            }
          : undefined,
        item.destinoFinal
          ? {
              icone: Feature.Reference.Destination.icon,
              fornecedor:
                item.destinoFinal.fornecedorNomeFantasia ||
                item.destinoFinal.fornecedorRazaoSocial ||
                item.destinoFinal.fornecedorNome ||
                '-',
            }
          : undefined,
      ].filter((entry) => !!entry),
    }));

  const headerContent = (
    <Group justify="space-between">
      <Group>
        <ProfileCardLink
          id={props.residue.idResiduo.toString()}
          name={props.residue.residuoCliente}
          nameSize="md"
          description={`Código IBAMA: ${props.residue.codigoIBAMA || '-'} | Perigoso: ${
            props.residue.residuoClientePerigoso ? 'Sim' : 'Não'
          }`}
          descriptionSize="sm"
          linkPrefix="residues"
          showLink={false}
        />
        <Group>
          {props.residue.estimativa ||
            (props.residue.minimoAceitavel && (
              <Stack gap="xs">
                {props.residue.estimativa && (
                  <Badge variant="outline" size="md" color={Feature.Home.Event.color}>
                    {`Estimativa: ${props.residue.estimativa} ${props.residue.estimativaUnidadeMedida1} / ${props.residue.estimativaUnidadeMedida2}`}
                  </Badge>
                )}
                {props.residue.minimoAceitavel && (
                  <Badge variant="outline" size="md" color="orange">
                    {`Mínimo Aceitável: ${props.residue.minimoAceitavel} ${props.residue.minimoAceitavelUnidadeMedida1} / ${props.residue.minimoAceitavelUnidadeMedida2}`}
                  </Badge>
                )}
              </Stack>
            ))}
          {props.residue.compra ||
            (props.residue.cobrarTolerancia && (
              <Stack gap="xs">
                {props.residue.compra ? (
                  <Badge variant="outline" size="md" color={Feature.Home.Event.color}>
                    Compra
                  </Badge>
                ) : null}
                {props.residue.cobrarTolerancia ? (
                  <Badge variant="outline" size="md" color={Feature.Home.Event.color}>
                    Cobrar Tolerâncias
                  </Badge>
                ) : null}
              </Stack>
            ))}
          {(props.event?.idEventoVeiculoCompartilhado || !!props.event?.resumoJSON?.veiculoCompartilhado) && (
            <Stack gap="xs">
              {props.event?.idEventoVeiculoCompartilhado ||
              !!props.event?.resumoJSON?.veiculoCompartilhado ? (
                <Badge variant="outline" size="md" color={Feature.Home.Event.color}>
                  Veículo Compartilhado
                </Badge>
              ) : null}
            </Stack>
          )}
        </Group>
      </Group>

      {!props.event && !shouldForceSingleCustomFlow && selectedPlan.cotacoes.length > 0 ? (
        <Select
          leftSection={<IconZoomMoney size={15} />}
          renderOption={({ option }) => {
            const quotationItem = quotationOptions.find((entry) => entry.value === option.value);
            return buildSelectQuotationItem(quotationItem as any);
          }}
          placeholder="Selecione..."
          data={transformComboBoxGroupDataFromV5toV7(quotationOptions as any)}
          value={quotationId?.toString()}
          onChange={(value) => {
            const nextQuotationId = value ? Number(value) : null;
            setQuotationId(nextQuotationId);
            if (setEventGeneralData && eventGeneralData) {
              setEventGeneralData({
                ...eventGeneralData,
                idEventoReferencia3: nextQuotationId,
              });
            }
          }}
          searchable
        />
      ) : null}
    </Group>
  );

  const legacyReferenceContent = (() => {
    if (!props.event) {
      return null;
    }

    if (
      currentReferencia === ReferenciaCodigo.ContratoResiduoAcondicionamento &&
      props.event.acondicionamento
    ) {
      return <EventPackagingView data={props.event.acondicionamento} event={props.event} />;
    }

    if (currentReferencia === ReferenciaCodigo.ContratoResiduoEquipamento && props.event.equipamento) {
      return <EventEquipmentView data={props.event.equipamento} event={props.event} />;
    }

    if (currentReferencia !== ReferenciaCodigo.ContratoResiduoPlano) {
      return null;
    }

    const tabs = [];

    if (props.event.resumoJSON?.veiculoCompartilhado) {
      tabs.push({
        key: 'VeiculoCompartilhado',
        label: (
          <Group>
            <ThemeIcon color={Feature.Home.Event.color} variant="outline">
              <IconTruck size={18} />
            </ThemeIcon>
            <Text size="md" c={Feature.Home.Event.color} fw={500}>
              Veículo Compartilhado
            </Text>
          </Group>
        ),
        children: (
          <EventVehicleView
            data={props.event.resumoJSON.veiculoCompartilhado}
            event={props.event}
            zIndex={props.zIndex}
          />
        ),
        forceRender: true,
      });
    }

    if (props.event.veiculo) {
      tabs.push({
        key: 'Veiculo',
        label: (
          <Group>
            <ThemeIcon color={Feature.Home.Event.color} variant="outline">
              <IconTruck size={18} />
            </ThemeIcon>
            <Text size="md" c={Feature.Home.Event.color} fw={500}>
              Veículo
            </Text>
          </Group>
        ),
        children: <EventVehicleView data={props.event.veiculo} event={props.event} zIndex={props.zIndex} />,
        forceRender: true,
      });
    }

    if (props.event.tratamento) {
      tabs.push({
        key: 'Tratamento',
        label: (
          <Group>
            <ThemeIcon color={Feature.Home.Event.color} variant="outline">
              <IconRecycle size={18} />
            </ThemeIcon>
            <Text size="md" c={Feature.Home.Event.color} fw={500}>
              Tratamento
            </Text>
          </Group>
        ),
        children: <EventTreatmentView data={props.event.tratamento} event={props.event} />,
        forceRender: true,
      });
    }

    if (props.event.destinoFinal) {
      tabs.push({
        key: 'DestinoFinal',
        label: (
          <Group>
            <ThemeIcon color={Feature.Home.Event.color} variant="outline">
              <IconTarget size={18} />
            </ThemeIcon>
            <Text size="md" c={Feature.Home.Event.color} fw={500}>
              Destino Final
            </Text>
          </Group>
        ),
        children: <EventDestinationView data={props.event.destinoFinal} event={props.event} />,
        forceRender: true,
      });
    }

    return (
      <Paper shadow="xs" p="md" withBorder>
        {props.event.idEventoVeiculoCompartilhado && (
          <>
            <Alert icon={<IconAlertTriangle size={16} />} title="Veículo Compartilhado" color="yellow">
              Esse evento tem o veículo compartilhado. Veja mais detalhes na seção Relações.
            </Alert>
            <Space h="md" />
          </>
        )}
        <Tabs items={tabs} />
      </Paper>
    );
  })();

  const summaryContent = (
    <Stack gap="sm">
      {autoImportedMissingSupplier && modoEdicao ? (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          O plano selecionado possui cotação sem fornecedor. A referência foi importada como personalizada
          para permitir o preenchimento.
        </Alert>
      ) : null}
      <ResiduePlanSummary
        data={summaryResidue}
        isEdit={isEditableReferenceFlow}
        requireSupplier={modoEdicao}
        planDisplayNumberFallback={findPlan(props.residue, props.selectedPlanId)?.idPropostaResiduoPlano}
        services={[]}
        hideHeader
        hidePackagingSection={!isPackagingReference}
        hideEquipmentSection={!isEquipmentReference}
        hideVehicleSection={isMobilizationEvent || (!modoEdicao && !displayVehiclePlanItem)}
        hideTreatmentSection={isMobilizationEvent || (!modoEdicao && !displayTreatmentPlanItem)}
        hideDestinationSection={isMobilizationEvent || (!modoEdicao && !displayDestinationPlanItem)}
        forcePackagingSection={isPackagingReference}
        forceEquipmentSection={isEquipmentReference}
        hideSectionSelectors={shouldHideCustomSectionSelectors}
        disableAddPackagingAction={!!resolvedPackagingPlanItem}
        disableAddEquipmentAction={!!resolvedEquipmentPlanItem}
        defaultShowPackagingSection={shouldForceSingleCustomFlow ? false : undefined}
        defaultShowEquipmentSection={shouldForceSingleCustomFlow ? false : undefined}
        defaultShowVehicleSection={defaultCustomVehicleSection}
        defaultShowTreatmentSection={shouldForceSingleCustomFlow ? false : undefined}
        defaultShowDestinationSection={defaultCustomDestinationSection}
        referenceData={props.referenceData}
        zIndex={props.zIndex}
        onPlanItemDelete={
          modoEdicao
            ? (_plan, planTab) => {
                if (planTab === 'Acondicionamento' || planTab === 'Acondicionamentos') {
                  setPackagingPlanItem(null);
                  setPackagingQuotationItem(null);
                  return;
                }
                if (planTab === 'Equipamento' || planTab === 'Equipamentos') {
                  setEquipmentPlanItem(null);
                  setEquipmentQuotationItem(null);
                  return;
                }
                if (planTab === 'Veiculos') {
                  setVehiclePlanItem(null);
                  setVehicleQuotationItem(null);
                  return;
                }
                if (planTab === 'Tratamento' || planTab === 'Tratamentos') {
                  setTreatmentPlanItem(null);
                  setTreatmentQuotationItem(null);
                  return;
                }
                if (planTab === 'DestinoFinal' || planTab === 'DestinoFinais') {
                  setDestinationPlanItem(null);
                  setDestinationQuotationItem(null);
                }
              }
            : undefined
        }
        onQuotationItemDelete={
          modoEdicao
            ? (_plan, _quotation, quotationTab) => {
                if (quotationTab === 'Acondicionamentos') {
                  setPackagingQuotationItem(null);
                  return;
                }
                if (quotationTab === 'Equipamentos') {
                  setEquipmentQuotationItem(null);
                  return;
                }
                if (quotationTab === 'Veiculos') {
                  setVehicleQuotationItem(null);
                  return;
                }
                if (quotationTab === 'Tratamentos') {
                  setTreatmentQuotationItem(null);
                  return;
                }
                if (quotationTab === 'DestinoFinais') {
                  setDestinationQuotationItem(null);
                }
              }
            : undefined
        }
        onVehicleSubmit={
          modoEdicao && !isPackagingReference && !isEquipmentReference && !isMobilizationEvent
            ? (_plan, item, action) => {
                setVehiclePlanItem(action === 'delete' ? null : item);
                if (action === 'delete') {
                  setVehicleQuotationItem(null);
                }
              }
            : undefined
        }
        onPackagingSubmit={
          modoEdicao
            ? (_plan, item, action) => {
                setPackagingPlanItem(action === 'delete' ? null : item);
                if (action === 'delete') {
                  setPackagingQuotationItem(null);
                }
              }
            : undefined
        }
        onEquipmentSubmit={
          modoEdicao
            ? (_plan, item, action) => {
                setEquipmentPlanItem(action === 'delete' ? null : item);
                if (action === 'delete') {
                  setEquipmentQuotationItem(null);
                }
              }
            : undefined
        }
        onTreatmentSubmit={
          modoEdicao && !isPackagingReference && !isEquipmentReference && !isMobilizationEvent
            ? (_plan, item, action) => {
                setTreatmentPlanItem(action === 'delete' ? null : item);
                if (action === 'delete') {
                  setTreatmentQuotationItem(null);
                }
              }
            : undefined
        }
        onDestinationSubmit={
          modoEdicao && !isPackagingReference && !isEquipmentReference && !isMobilizationEvent
            ? (_plan, item, action) => {
                setDestinationPlanItem(action === 'delete' ? null : item);
                if (action === 'delete') {
                  setDestinationQuotationItem(null);
                }
              }
            : undefined
        }
        onVehicleQuotationSubmit={
          modoEdicao && !isPackagingReference && !isEquipmentReference && !isMobilizationEvent
            ? (_plan, _quotation, _refItem, item, action) =>
                setVehicleQuotationItem(action === 'delete' ? null : item)
            : undefined
        }
        onPackagingQuotationSubmit={
          modoEdicao
            ? (_plan, _quotation, _refItem, item, action) =>
                setPackagingQuotationItem(action === 'delete' ? null : item)
            : undefined
        }
        onEquipmentQuotationSubmit={
          modoEdicao
            ? (_plan, _quotation, _refItem, item, action) =>
                setEquipmentQuotationItem(action === 'delete' ? null : item)
            : undefined
        }
        onTreatmentQuotationSubmit={
          modoEdicao && !isPackagingReference && !isEquipmentReference && !isMobilizationEvent
            ? (_plan, _quotation, _refItem, item, action) =>
                setTreatmentQuotationItem(action === 'delete' ? null : item)
            : undefined
        }
        onDestinationQuotationSubmit={
          modoEdicao && !isPackagingReference && !isEquipmentReference && !isMobilizationEvent
            ? (_plan, _quotation, _refItem, item, action) =>
                setDestinationQuotationItem(action === 'delete' ? null : item)
            : undefined
        }
      />
    </Stack>
  );

  return (
    <Paper shadow="xs" p="md" withBorder>
      {!modoEdicao && legacyReferenceContent ? (
        <ReferenceFlipCard
          header={headerContent}
          frontContent={summaryContent}
          backContent={legacyReferenceContent}
        />
      ) : (
        <>
          {headerContent}
          <Space h="sm" />
          {summaryContent}
        </>
      )}
    </Paper>
  );
});

export default EventResidueReferenceEditor;
